import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { formatDistanceToNowStrict } from 'date-fns'
import { Link as RouterLink } from 'react-router-dom'

import { useAuth0 } from '@/utils/auth/auth0'
import {
  apiUrl,
  type EditStatus,
  type ProposedEdit,
  statusColor,
  actionColor,
} from './_shared'
import { EditReviewModal } from './EditReviewModal'

export interface ProposedEditsPanelProps {
  /** At least one of these must be set. */
  funderUuid?: string
  opportunityUuid?: string
}

/**
 * List of proposed edits scoped to one funder or opportunity, rendered
 * inline on the funder/opportunity show pages. Clicking a row opens the
 * shared `EditReviewModal` — the same modal the full /proposed-edits
 * page uses — so admins get the full diff / amend / questions editor /
 * regen panel UI without leaving the show page. The "Open in full view"
 * link in the header is for batch review across all edits.
 */
export const ProposedEditsPanel: React.FC<ProposedEditsPanelProps> = ({
  funderUuid,
  opportunityUuid,
}) => {
  const { getAccessTokenSilently } = useAuth0()
  const [items, setItems] = useState<ProposedEdit[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<ProposedEdit | null>(null)

  const params = new URLSearchParams()
  if (funderUuid) params.append('funder_uuid', funderUuid)
  if (opportunityUuid) params.append('opportunity_uuid', opportunityUuid)
  const queryString = params.toString()

  const load = useCallback(async () => {
    if (!funderUuid && !opportunityUuid) return
    setLoading(true)
    setError(null)
    try {
      const res = await axios.get<{ items: ProposedEdit[]; total: number }>(
        `${apiUrl}/proposed-edits/?${queryString}&limit=200`,
      )
      setItems(res.data.items)
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { detail?: string } }
        message?: string
      }
      setError(err?.response?.data?.detail ?? err?.message ?? 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [queryString, funderUuid, opportunityUuid])

  useEffect(() => {
    load()
  }, [load])

  // Live refresh via the shared SSE stream.
  useEffect(() => {
    let es: EventSource | null = null
    let cancelled = false
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null

    const open = async () => {
      try {
        const token = await getAccessTokenSilently()
        if (cancelled) return
        es = new EventSource(
          `${apiUrl}/proposed-edits/stream?token=${encodeURIComponent(token)}`,
        )
        es.onmessage = () => load()
        es.onerror = () => {
          if (es && es.readyState === EventSource.CLOSED) {
            es.close()
            es = null
            if (!cancelled) reconnectTimer = setTimeout(open, 3000)
          }
        }
      } catch (err) {
        console.warn('proposed-edits panel: SSE open failed', err)
      }
    }
    open()
    return () => {
      cancelled = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (es) es.close()
    }
  }, [load, getAccessTokenSilently])

  // Keep `selected` in sync with the latest `items` so post-approve refresh
  // shows the new status without needing to reopen the row.
  useEffect(() => {
    if (!selected) return
    const refreshed = items.find((i) => i.id === selected.id)
    if (refreshed && refreshed !== selected) setSelected(refreshed)
  }, [items, selected])

  if (!funderUuid && !opportunityUuid) {
    return (
      <Alert severity="error">
        ProposedEditsPanel requires either funderUuid or opportunityUuid.
      </Alert>
    )
  }

  const scopeLabel = funderUuid ? 'this funder' : 'this opportunity'
  const fullViewHref = `/proposed-edits?${queryString}`

  const counts = items.reduce(
    (acc, item) => {
      acc[item.status] = (acc[item.status] ?? 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Typography variant="h6">Proposed Edits for {scopeLabel}</Typography>
        <Chip
          label={`${items.length} total`}
          size="small"
          variant="outlined"
        />
        {(
          ['pending', 'approved', 'rejected', 'withdrawn'] as EditStatus[]
        ).map((s) =>
          counts[s] ? (
            <Chip
              key={s}
              label={`${counts[s]} ${s}`}
              size="small"
              color={statusColor[s]}
            />
          ) : null,
        )}
        <Box sx={{ ml: 'auto' }}>
          <Button
            component={RouterLink}
            to={fullViewHref}
            size="small"
            variant="outlined"
          >
            Open in full view
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && items.length === 0 && (
        <Typography color="text.secondary">
          No proposed edits yet for {scopeLabel}.
        </Typography>
      )}

      {!loading && !error && items.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Change</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Reviewed</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Review comment</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      #{item.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.status}
                      size="small"
                      color={statusColor[item.status]}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2">
                        {item.resource_type}
                      </Typography>
                      <Chip
                        label={item.action}
                        size="small"
                        color={actionColor[item.action]}
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDistanceToNowStrict(
                        new Date(item.created_at + 'Z'),
                        { addSuffix: true },
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.submitted_by_name ?? item.submitted_by_auth0_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.reviewed_at ? (
                      <>
                        <Typography variant="body2">
                          {formatDistanceToNowStrict(
                            new Date(item.reviewed_at + 'Z'),
                            { addSuffix: true },
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.reviewed_by_name ?? item.reviewed_by_auth0_id}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 240,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.note ?? ''}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 240,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.review_comment ?? item.withdraw_reason ?? ''}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant={
                        item.status === 'pending' ? 'contained' : 'outlined'
                      }
                      onClick={() => setSelected(item)}
                    >
                      {item.status === 'pending' ? 'Review' : 'View'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/*
        The shared modal: same diff / amend / questions editor / regen panel
        UI as the full /proposed-edits page. `onChanged={load}` refetches
        this scoped list after a successful approve / reject / amend so the
        row reflects the new status without a full page refresh.
      */}
      <EditReviewModal
        edit={selected}
        onClose={() => setSelected(null)}
        onChanged={load}
      />
    </Box>
  )
}
